import type { ThemeId } from '../persistence/ProfileRepository'

export type ShellId =
  | 'ion-core' | 'facet' | 'reactor-cage' | 'nova-prism' | 'ghost-lattice' | 'vector'
  | 'helix' | 'eclipse' | 'pulse' | 'rift' | 'sentinel' | 'apex'
export type TrailId = 'ion' | 'pulse' | 'ember' | 'prism' | 'ghost' | 'vector' | 'rift' | 'apex'

export interface MasteryState {
  readonly [metric: string]: number | undefined
}

export interface UnlockState {
  readonly bestDistance: number
  readonly mastery: MasteryState
}

export interface UnlockRequirement {
  readonly distance?: number
  readonly mastery?: { readonly metric: string; readonly value: number }
}

export interface CosmeticDefinition<Id extends string> {
  readonly id: Id
  readonly name: string
  readonly description: string
  readonly unlock: readonly UnlockRequirement[]
}

const shell = (id: ShellId, name: string, distance: number, metric?: string, value?: number): CosmeticDefinition<ShellId> => ({
  id,
  name,
  description: `${name} ceramic-energy shell`,
  unlock: [
    { distance },
    ...(metric === undefined || value === undefined ? [] : [{ mastery: { metric, value } }]),
  ],
})

const trail = (id: TrailId, name: string, distance: number, metric?: string, value?: number): CosmeticDefinition<TrailId> => ({
  id,
  name,
  description: `${name} velocity wake`,
  unlock: [
    { distance },
    ...(metric === undefined || value === undefined ? [] : [{ mastery: { metric, value } }]),
  ],
})

export const COSMETICS = {
  shells: [
    shell('ion-core', 'Ion Core', 0),
    shell('facet', 'Facet', 3_000, 'cleanLandings', 12),
    shell('reactor-cage', 'Reactor Cage', 4_000, 'nearMisses', 20),
    shell('nova-prism', 'Nova Prism', 6_000, 'dailySeals', 5),
    shell('ghost-lattice', 'Ghost Lattice', 10_000, 'ghostWins', 3),
    shell('vector', 'Vector', 2_500, 'rivalWins', 1),
    shell('helix', 'Helix', 4_500, 'cleanLandings', 25),
    shell('eclipse', 'Eclipse', 7_500, 'nearMisses', 50),
    shell('pulse', 'Pulse', 1_500, 'runs', 10),
    shell('rift', 'Rift', 8_500, 'sharedGhosts', 1),
    shell('sentinel', 'Sentinel', 9_000, 'rivalWins', 10),
    shell('apex', 'Apex', 10_000, 'dailySeals', 30),
  ],
  trails: [
    trail('ion', 'Ion', 0),
    trail('pulse', 'Pulse', 500),
    trail('ember', 'Ember', 1_500),
    trail('prism', 'Prism', 3_000, 'cleanLandings', 10),
    trail('ghost', 'Ghost', 6_000, 'ghostWins', 1),
    trail('vector', 'Vector', 4_000, 'rivalWins', 3),
    trail('rift', 'Rift', 8_000, 'nearMisses', 40),
    trail('apex', 'Apex', 10_000, 'dailySeals', 10),
  ],
  themes: [
    { id: 'ion', name: 'Ion', description: 'Cyan computational abyss', unlock: [{ distance: 0 }] },
    { id: 'reactor', name: 'Reactor', description: 'Gold industrial core', unlock: [{ distance: 1_500 }] },
    { id: 'nova', name: 'Nova', description: 'Violet stellar fracture', unlock: [{ distance: 6_000 }] },
    { id: 'ghost', name: 'Ghost', description: 'Silver spectral array', unlock: [{ distance: 10_000 }] },
  ] satisfies readonly CosmeticDefinition<ThemeId>[],
} as const

export function isCosmeticUnlocked(definition: CosmeticDefinition<string>, state: UnlockState): boolean {
  return definition.unlock.some((requirement) => {
    if (requirement.distance !== undefined && state.bestDistance >= requirement.distance) return true
    if (requirement.mastery !== undefined) {
      return (state.mastery[requirement.mastery.metric] ?? 0) >= requirement.mastery.value
    }
    return false
  })
}
